import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useUserStore } from '@/stores/userStore';
import { getParticipantFromAppsScript } from '@/services/registration';

type VerificationStatus = 'verifying' | 'success' | 'error';

export default function VerifyEmailPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setParticipant } = useUserStore();

  const [status, setStatus] = useState<VerificationStatus>('verifying');
  const [message, setMessage] = useState('Verifying your email...');
  const [participantName, setParticipantName] = useState('');
  const [participantPin, setParticipantPin] = useState('');

  useEffect(() => {
    const checkVerification = async () => {
      const verified = searchParams.get('verified');
      const id = searchParams.get('id');
      const name = searchParams.get('name');
      const pin = searchParams.get('pin');
      const error = searchParams.get('error');

      // Handle error from Apps Script
      if (error) {
        setStatus('error');
        if (error === 'invalid_token') {
          setMessage('Invalid or expired verification link. Please request a new one.');
        } else if (error === 'missing_params') {
          setMessage('Invalid verification link. Please check your email.');
        } else {
          setMessage('Verification failed. Please try again.');
        }
        return;
      }

      // Coming back from Apps Script with successful verification
      if (verified === 'true' && id) {
        setParticipantName(name ? decodeURIComponent(name) : '');
        setParticipantPin(pin || '');

        // Try to fetch full participant data from Apps Script
        try {
          const participantData = await getParticipantFromAppsScript(id);

          if (participantData) {
            setParticipant({
              ...participantData,
              emailVerified: true,
            });
          }
        } catch (err) {
          console.error('Error fetching participant data:', err);
        }

        setStatus('success');
        setMessage('Your email has been verified! Check your inbox for your ticket email with QR code.');
        return;
      }

      // User navigated here directly without params
      setStatus('error');
      setMessage('No verification data found. Please click the link in your verification email.');
    };

    checkVerification();
  }, [searchParams, setParticipant]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-cream via-white to-cream/50 flex items-center justify-center px-6">
      <div className="max-w-md w-full">
        <div className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-large border border-white p-12 text-center">
          {/* Icon */}
          <div className="mb-8">
            {status === 'verifying' && (
              <div className="w-20 h-20 mx-auto">
                <svg className="animate-spin text-crimson" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              </div>
            )}

            {status === 'success' && (
              <div className="w-20 h-20 mx-auto bg-green-100 rounded-full flex items-center justify-center">
                <svg className="w-10 h-10 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
            )}

            {status === 'error' && (
              <div className="w-20 h-20 mx-auto bg-red-100 rounded-full flex items-center justify-center">
                <svg className="w-10 h-10 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
            )}
          </div>

          <h1 className="text-3xl font-display font-bold text-earth-brown mb-4">
            {status === 'verifying' && 'Verifying Email'}
            {status === 'success' && 'Verified!'}
            {status === 'error' && 'Verification Issue'}
          </h1>

          {participantName && status === 'success' && (
            <p className="text-lg text-crimson font-semibold mb-2">
              Welcome, {participantName.split(' ')[0]}!
            </p>
          )}

          <p className="text-earth-brown/70 mb-8 leading-relaxed">{message}</p>

          {/* Success state - Show PIN and info about ticket email */}
          {status === 'success' && (
            <div className="space-y-6">
              {participantPin && (
                <div className="bg-crimson/10 rounded-2xl p-6 border-2 border-crimson/20">
                  <p className="text-xs uppercase tracking-wide text-earth-brown/60 font-semibold mb-2">
                    Your Check-in PIN
                  </p>
                  <p className="text-4xl font-bold font-mono text-crimson tracking-wider">
                    {participantPin}
                  </p>
                  <p className="text-xs text-earth-brown/60 mt-2">
                    Save this PIN as backup for event check-in
                  </p>
                </div>
              )}

              <div className="bg-gradient-to-br from-crimson/5 to-crimson/10 rounded-2xl p-6 border border-crimson/20 text-left">
                <h3 className="text-sm font-bold text-earth-brown mb-3 flex items-center gap-2">
                  <svg className="w-4 h-4 text-crimson" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                    <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                  </svg>
                  Check Your Email
                </h3>
                <p className="text-sm text-earth-brown/70 leading-relaxed">
                  We&apos;ve sent your QR code ticket to your email. You can also download it as an image from the ticket email.
                </p>
              </div>

              <button
                onClick={() => {
                  const id = searchParams.get('id');
                  navigate(id ? `/ticket/${id}` : '/');
                }}
                className="w-full px-6 py-4 bg-crimson text-white font-semibold rounded-2xl hover:bg-crimson-dark hover:shadow-large transition-all duration-300 active:scale-95"
              >
                View My Ticket
              </button>
              <button
                onClick={() => navigate('/')}
                className="w-full px-6 py-4 bg-white text-earth-brown font-semibold rounded-2xl border-2 border-earth-tan/50 hover:border-earth-tan hover:bg-earth-tan/5 transition-all duration-300"
              >
                Return to Home
              </button>
            </div>
          )}

          {/* Error state */}
          {status === 'error' && (
            <div className="space-y-4">
              <button
                onClick={() => navigate('/register')}
                className="w-full px-6 py-4 bg-crimson text-white font-semibold rounded-2xl hover:bg-crimson-dark hover:shadow-large transition-all duration-300 active:scale-95"
              >
                Register Now
              </button>

              <button
                onClick={() => navigate('/')}
                className="w-full px-6 py-4 bg-white text-earth-brown font-semibold rounded-2xl border-2 border-earth-tan/50 hover:border-earth-tan hover:bg-earth-tan/5 transition-all duration-300"
              >
                Return to Home
              </button>
            </div>
          )}
        </div>

        {/* Help text */}
        <div className="mt-6 text-center">
          <p className="text-sm text-earth-brown/50">
            Need help? Contact us at{' '}
            <a href="mailto:feastmagnoliahwr@gmail.com" className="text-crimson hover:underline font-semibold">
              feastmagnoliahwr@gmail.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
